import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { settingsApi, type SettingsPatch, type TenantSettings } from "@api/modules/settings.api";
import { useToast } from "@hooks/useToast";
import { useAuthStore } from "@stores/auth/authStore";
import { useCurrencyStore } from "@stores/currency";
import { useLanguageStore } from "@stores/language";
import { apiErrorText } from "@lib/apiError";
import {
    normalizeCurrency,
    normalizeLanguage,
    type CurrencyCode,
    type LanguageCode,
} from "@lib/i18n/languages";

export const SETTINGS_QUERY_KEY = ["settings"] as const;

export type SanitizedSettings = Omit<TenantSettings, "language" | "currency"> & {
    language: LanguageCode;
    currency: CurrencyCode;
};

/** Reads the saved settings defensively (older accounts may hold values the app does not ship). */
export function sanitizeSettings(raw: Partial<TenantSettings> | undefined): SanitizedSettings {
    return {
        language: normalizeLanguage(raw?.language),
        currency: normalizeCurrency(raw?.currency),
        theme: raw?.theme === "dark" ? "dark" : "light",
        emailNotifications: raw?.emailNotifications ?? true,
        pushNotifications: raw?.pushNotifications ?? true,
        shiftReminders: raw?.shiftReminders ?? false,
        weeklyReports: raw?.weeklyReports ?? false,
        lowStockThreshold: Number(raw?.lowStockThreshold ?? 10),
        companyName: raw?.companyName ?? null,
        companyLogo: raw?.companyLogo ?? null,
        timezoneOffset: Number(raw?.timezoneOffset ?? 0),
    };
}

/**
 * Keeps the app's language and currency in step with the saved account settings. Theme is a
 * per-device choice (the header toggle), so it is not forced from the server on load.
 */
export function useServerSettingsSync() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const billingOnly = useAuthStore((s) => s.billingOnly);
    const setLanguage = useLanguageStore((s) => s.setLanguage);
    const setCurrency = useCurrencyStore((s) => s.setCurrency);

    const query = useQuery({
        queryKey: SETTINGS_QUERY_KEY,
        enabled: isAuthenticated && !billingOnly,
        staleTime: 5 * 60_000,
        queryFn: async () => sanitizeSettings((await settingsApi.get()).settings),
    });

    React.useEffect(() => {
        if (!query.data) return;
        setLanguage(query.data.language);
        setCurrency(query.data.currency);
    }, [query.data, setLanguage, setCurrency]);

    return query;
}

export function useTenantSettingsMutation(options: { quiet?: boolean } = {}) {
    const { t } = useTranslation();
    const qc = useQueryClient();
    const { success, error } = useToast();
    const setLanguage = useLanguageStore((s) => s.setLanguage);
    const setCurrency = useCurrencyStore((s) => s.setCurrency);

    return useMutation({
        mutationFn: (patch: SettingsPatch) => settingsApi.update(patch),
        onSuccess: (res) => {
            const saved = sanitizeSettings(res.settings);
            qc.setQueryData(SETTINGS_QUERY_KEY, saved);
            setLanguage(saved.language);
            setCurrency(saved.currency);
            // The dashboard's low-stock list depends on the threshold.
            void qc.invalidateQueries({ queryKey: ["dashboard"] });
            if (!options.quiet) success(t("settings.saved"));
        },
        onError: (e: unknown) => error(t("settings.saveFailed"), { description: apiErrorText(t, e) }),
    });
}
